import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary';
import Layout from './components/Layout';

// Dispatch event when app is ready for prerendering
const PrerenderReady = () => {
  useEffect(() => {
    // Delay slightly to ensure content is rendered
    const timer = setTimeout(() => {
      document.dispatchEvent(new Event('render-event'));
    }, 100);
    return () => clearTimeout(timer);
  }, []);
  return null;
};

// Lazy load pages
const Landing = lazy(() => import('./pages/Landing'));
const Home = lazy(() => import('./pages/Home'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Terms = lazy(() => import('./pages/Terms'));
const About = lazy(() => import('./pages/About'));
const Contact = lazy(() => import('./pages/Contact'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const LifePathCalculator = lazy(() => import('./pages/LifePathCalculator'));
const MoonPhaseCalculator = lazy(() => import('./pages/MoonPhaseCalculator'));
const BirthChart = lazy(() => import('./pages/BirthChart'));
const ChineseZodiacCalculator = lazy(() => import('./pages/ChineseZodiacCalculator'));
const MonthVerses = lazy(() => import('./pages/MonthVerses'));
const BirthdayBibleVerse = lazy(() => import('./pages/BirthdayBibleVerse'));
const BirthdayMeaning = lazy(() => import('./pages/BirthdayMeaning'));
const NotFound = lazy(() => import('./pages/NotFound'));
const ZodiacAnimalPage = lazy(() => import('./pages/ZodiacAnimalPage'));
const LifePathNumberPage = lazy(() => import('./pages/LifePathNumberPage'));
const MoonPhasePage = lazy(() => import('./pages/MoonPhasePage'));

// Loading component
const Loading = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#F8F7FF]">
    <div className="animate-pulse text-[#4F46E5]">Loading...</div>
  </div>
);

import { HelmetProvider } from 'react-helmet-async';

import GTMTracker from './components/GTMTracker';

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <Router>
          <GTMTracker />
          <Suspense fallback={<Loading />}>
            <Routes>
              <Route element={<Layout />}>
                <Route path="/" element={<Landing />} />
                <Route path="/calculators/birthverse" element={<Home />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/calculators/life-path" element={<LifePathCalculator />} />
                <Route path="/calculators/moon-phase" element={<MoonPhaseCalculator />} />
                <Route path="/calculators/birth-chart" element={<BirthChart />} />
                <Route path="/calculators/chinese-zodiac" element={<ChineseZodiacCalculator />} />
                <Route path="/bible-verses/:month" element={<MonthVerses />} />
                <Route path="/birthday-bible-verse" element={<BirthdayBibleVerse />} />
                <Route path="/birthday-meaning/:date" element={<BirthdayMeaning />} />
                <Route path="/chinese-zodiac/:animal" element={<ZodiacAnimalPage />} />
                <Route path="/life-path-number/:number" element={<LifePathNumberPage />} />
                <Route path="/moon-phase/:phase" element={<MoonPhasePage />} />
                <Route path="*" element={<NotFound />} />
              </Route>
            </Routes>
            <PrerenderReady />
          </Suspense>
        </Router>
      </HelmetProvider>
    </ErrorBoundary>
  );
};

export default App;
